/**
 * Permalink / linkable-citation URL helpers (Phase-2 permalink-citations chunk).
 *
 * A citation permalink is a STABLE deep-link that survives edits: it points at a
 * document AND a specific anchored comment, and — because the anchor is rebased
 * at commit-time server-side — always resolves to the comment's CURRENT cited
 * span. The URL carries only the addressing (`/w/:slug/doc/:id?comment=<id>`);
 * the durable selector is fetched at open-time via the resolve endpoint.
 *
 * Pure functions over the native URL / URLSearchParams API — no framework, no
 * DOM. (RULE ZERO: there is no @norkron web-* "permalink" primitive to reuse;
 * URL building is a browser-standard capability, not a re-solved lib domain.)
 */

/** The query-string key that carries the cited comment id. */
export const CITATION_PARAM = "comment";

/**
 * The in-app relative path a citation permalink navigates to. Matches the
 * `/w/:slug/doc/:id` route; the cited comment rides in `?comment=<id>`.
 */
export function buildCitationPath(
  slug: string,
  docId: string,
  commentId: number | string,
): string {
  const params = new URLSearchParams();
  params.set(CITATION_PARAM, String(commentId));
  return `/w/${encodeURIComponent(slug)}/doc/${encodeURIComponent(
    String(docId),
  )}?${params.toString()}`;
}

/**
 * The absolute, copy-to-clipboard permalink. `origin` is normally
 * `window.location.origin` (single-origin app). Delegates the relative path to
 * `buildCitationPath` so the two forms never drift.
 */
export function buildCitationUrl(
  origin: string,
  slug: string,
  docId: string,
  commentId: number | string,
): string {
  return new URL(buildCitationPath(slug, docId, commentId), origin).toString();
}

/**
 * Extract the cited comment id from a location search string (with or without a
 * leading `?`). Returns a positive integer, or null when absent / malformed —
 * so a hand-mangled `?comment=abc` is ignored rather than throwing. The id is
 * numeric because comment ids are `BIGSERIAL` (see the backend contract).
 */
export function parseCitationParam(search: string): number | null {
  const raw = new URLSearchParams(search).get(CITATION_PARAM);
  if (raw == null || raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

/**
 * True when a location search carries a citation deep-link. Convenience for a
 * host component that wants to branch before resolving.
 */
export function hasCitationParam(search: string): boolean {
  return parseCitationParam(search) !== null;
}
